import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'

/**
 * Error types for element ID resolution
 */
export type ProductTypeIdResolutionError =
    | 'PT_NOT_FOUND'
    | 'CONTENT_NODE_NOT_FOUND'
    | 'MISSING_DTV_ID'

/**
 * Selected project element node structure
 */
export interface SelectedProjectElementNode {
    elementType?: string | null
    elementId?: string | null
    [key: string]: unknown
}

/**
 * Content item view node structure with elements
 */
export interface ContentItemViewNode {
    id?: string
    dtvItemId?: string
    elements?: Array<{ id?: string }>
    [key: string]: unknown
}

/**
 * Parameters for resolving element IDs
 */
export interface ResolveProductTypeIdsParams {
    /** Currently selected project element node (Family, PT, or Article) */
    selectedProjectElementNode: SelectedProjectElementNode | null | undefined
    /** All content item view nodes */
    contentItemViewNodes: ContentItemViewNode[]
    /** Content item ID being edited */
    contentItemId: string
    /** Elements added to this family - includes both PTs and Articles with drawing designations */
    familyAddedProductTypes: Partial<ProjectElementNodeWithDrawingDesignation>[]
    /** Parent family ID (used when PT/Article is selected directly) */
    parentFamilyId?: string
}

/**
 * Result of element ID resolution
 */
export interface ResolvedProductTypeIds {
    /** Project-specific element ID (PT or Article) */
    productTypeElementId: string
    /** Product Family element ID */
    productFamilyId: string
    /** Library element ID - stable across add/remove cycles, used for caching */
    baseElementId: string
    /** Error type if resolution failed */
    error?: ProductTypeIdResolutionError
}

/**
 * Checks if the selected element is at Family level (vs PT/Article level)
 */
export const isFamilyLevelSelection = (
    selectedProjectElementNode: SelectedProjectElementNode | null | undefined
): boolean => selectedProjectElementNode?.elementType === 'PRODUCTFAMILY'

/**
 * Resolves element IDs for drawing designation save operation.
 * Handles both Product Types and Articles uniformly.
 *
 * - **Direct selection** (PT/Article selected): uses `selectedProjectElementNode.elementId`
 * - **Family selection** (viewing PT/Article in Family): resolves via `familyAddedProductTypes`
 *   by matching `contentItemId` → `dtvItemId` → `baseElementId` → `elementId`
 */
export const resolveProductTypeIds = ({
    selectedProjectElementNode,
    contentItemViewNodes,
    contentItemId,
    familyAddedProductTypes,
    parentFamilyId,
}: ResolveProductTypeIdsParams): ResolvedProductTypeIds => {
    const isFamilySelected = isFamilyLevelSelection(selectedProjectElementNode)

    // DIRECT SELECTION: PT or Article selected directly in elements panel
    // Use the selected element's ID directly with its parent family ID
    if (!isFamilySelected) {
        return {
            productTypeElementId: selectedProjectElementNode?.elementId || '',
            productFamilyId: parentFamilyId || '',
            baseElementId:
                ((selectedProjectElementNode as Record<string, unknown>)
                    ?.baseElementId as string) || '',
        }
    }

    // FAMILY SELECTION: Resolve PT/Article ID via familyAddedProductTypes
    // Step 1: Find content node containing the contentItemId being edited
    const nodeWithContent = contentItemViewNodes.find((n) =>
        n.elements?.some((el) => el.id === contentItemId)
    )

    if (!nodeWithContent) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            baseElementId: '',
            error: 'CONTENT_NODE_NOT_FOUND',
        }
    }

    // Step 2: Verify the node has a dtvItemId (base element ID for matching)
    if (!nodeWithContent.dtvItemId) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            baseElementId: '',
            error: 'MISSING_DTV_ID',
        }
    }

    // Step 3: Match node's dtvItemId with familyAddedProductTypes to get project element ID
    const matchedElement = familyAddedProductTypes.find(
        (element) => element.baseElementId === nodeWithContent.dtvItemId
    )

    if (!matchedElement?.elementId) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            baseElementId: nodeWithContent.dtvItemId,
            error: 'PT_NOT_FOUND',
        }
    }

    // Step 4: Return resolved IDs
    return {
        productTypeElementId: matchedElement.elementId,
        productFamilyId: selectedProjectElementNode?.elementId || '',
        baseElementId: nodeWithContent.dtvItemId,
    }
}

/**
 * Human-readable error messages for element ID resolution errors
 */
export const PRODUCT_TYPE_ID_ERROR_MESSAGES: Record<
    ProductTypeIdResolutionError,
    string
> = {
    PT_NOT_FOUND: 'Unable to identify element for saving',
    CONTENT_NODE_NOT_FOUND: 'Content node not found',
    MISSING_DTV_ID: 'Missing element identifier',
}
